export type Currency = {
  code: string;
  symbol: string;
  name: string;
};

export type TicketTier = {
  id: string;
  name: string;
  qty: number;
  priceMin: number;
  priceMax: number;
};

export type SponsorTier = {
  id: string;
  name: string;
  qty: number;
  priceMin: number;
  priceMax: number;
};

export type Addon = {
  id: string;
  name: string;
  enabled: boolean;
  price: number;
  // Optional per-unit multiplier (e.g. number of tracks for a stage sponsor).
  multiplier?: number;
  multiplierLabel?: string;
};

export type CostItem = {
  id: string;
  name: string;
  amount: number;
};

// Union of every cost category across templates. The schema is flat —
// every EventData carries all keys (empty arrays by default) — but the UI
// only renders the categories relevant to the event's template.
export type CostCategory =
  // Conference categories
  | 'venue'
  | 'catering'
  | 'security'
  | 'av'
  | 'marketing'
  | 'additional'
  // Meetup-only categories
  | 'refreshments'
  | 'food'
  | 'swags'
  | 'materials'
  | 'speakers';

export type CostCategoryDef = { key: CostCategory; label: string };

export const CONFERENCE_COST_CATEGORIES: CostCategoryDef[] = [
  { key: 'venue', label: 'Venue' },
  { key: 'catering', label: 'Catering' },
  { key: 'security', label: 'Security' },
  { key: 'av', label: 'Audio/Video' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'additional', label: 'Additional costs' },
];

export const MEETUP_COST_CATEGORIES: CostCategoryDef[] = [
  { key: 'venue', label: 'Venue' },
  { key: 'refreshments', label: 'Refreshments' },
  { key: 'food', label: 'Food' },
  { key: 'swags', label: 'Swag' },
  { key: 'materials', label: 'Materials' },
  { key: 'speakers', label: 'Speakers' },
];

export function costCategoriesFor(t: EventType): CostCategoryDef[] {
  return t === 'meetup' ? MEETUP_COST_CATEGORIES : CONFERENCE_COST_CATEGORIES;
}

// Back-compat alias for any older imports.
export const COST_CATEGORIES = CONFERENCE_COST_CATEGORIES;

export type ScenarioName =
  | 'pessimistic'
  | 'realistic'
  | 'optimistic'
  | 'custom';

export type AddonsScenario = 'as-planned' | 'all-on' | 'all-off';

// Custom scenario stores per-tier overrides keyed by tier id, plus the two
// global knobs (add-ons strategy and cost multiplier). Missing tier ids
// default to 1.0 (no change) at projection time.
export type CustomScenario = {
  ticketFill: Record<string, number>;
  sponsorFill: Record<string, number>;
  addonsEnabled: AddonsScenario;
  costMultiplier: number;
};

export type Scenario = {
  active: ScenarioName;
  custom: CustomScenario;
};

// Keys identifying a cashflow payment plan. Includes every revenue stream
// and every cost category — so the user can override timing per "thing
// that creates a payment".
export type CashflowPlanKey =
  | 'tickets'
  | 'sponsors'
  | 'addons'
  | CostCategory;

export type PaymentMilestoneDTO = {
  pct: number;
  daysBeforeEvent: number;
};

// Whether VAT is tracked for this event.
// - 'net':   amounts entered are simple values; no VAT computation, no labels.
// - 'gross': amounts entered are NET; the app multiplies by `vatRate` to
//   produce VAT and gross figures and displays them broken out everywhere.
export type VatMode = 'net' | 'gross';

export type EventData = {
  tickets: TicketTier[];
  sponsors: SponsorTier[];
  addons: Addon[];
  costs: Record<CostCategory, CostItem[]>;
  scenario: Scenario;
  // Per-category timing overrides. Missing keys fall back to the bundled
  // default plan in cashflow-defaults.ts.
  cashflowPlans: Partial<Record<CashflowPlanKey, PaymentMilestoneDTO[]>>;
  vatMode: VatMode;
  // VAT rate as a fraction (0..1). Applied uniformly to all line items
  // when vatMode === 'gross'. Default 0.23 (Polish standard rate).
  vatRate: number;
};

export type EventType = 'conference' | 'meetup';

// Meetup-specific metadata. None of these are required during creation
// (forms can submit partial values), but the keys are stable so the
// future meetup editor can rely on them.
export type MeetupMeta = {
  number?: number;        // edition / "#12 in the series"
  hour?: string;          // "19:00"
  max_participants?: number;
  talk_count?: number;
};

export type EventMeta = {
  id?: string;
  name: string;
  event_type: EventType;
  country_code: string;
  city: string;
  start_date?: string | null;
  end_date?: string | null;
  conference_days: number;
  workshop_days: number;
  currency: string;
  meetup_meta?: MeetupMeta;
};

export type CitySuggestion = {
  city: string;
  state?: string;
  country_code: string;
  country?: string;
  display_name: string;
  lat?: number;
  lon?: number;
};

export type EventRecord = EventMeta & {
  id: string;
  data: EventData;
  created_at?: string;
  updated_at?: string;
};

export type EventSummary = EventMeta & {
  id: string;
  updated_at: string;
  archived_at?: string;
};

export type EventListStatus = 'active' | 'archived' | 'all';
export type EventListType = 'conference' | 'meetup' | 'all';

export type AuthUser = {
  id: string;
  email: string;
};
